import { Component, Input } from '@angular/core';
import { NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApplicationQuotaOverrides } from '../../../models/application.model';

// One override as the form edits it: unticked, the connection inherits the consumer's policy.
export interface QuotaOverrideField {
  custom: boolean;
  value: number | null;
}

export interface QuotaOverridesForm {
  quotaMax: QuotaOverrideField;
  quotaRenewalRate: QuotaOverrideField;
}

// The lowest each override may be: -1 is an unlimited quota_max.
const QUOTA_MAX_MINIMUM = -1;
const QUOTA_RENEWAL_RATE_MINIMUM = 0;
const UNLIMITED_QUOTA_MAX = -1;

const QUOTA_MAX_NAME = 'The shopping allowance';
const QUOTA_RENEWAL_RATE_NAME = 'The look-to-book ratio';

export function emptyQuotaOverridesForm(): QuotaOverridesForm {
  return {
    quotaMax: { custom: false, value: null },
    quotaRenewalRate: { custom: false, value: null },
  };
}

// The form for editing a connection's current overrides.
export function quotaOverridesFormOf(overrides: ApplicationQuotaOverrides): QuotaOverridesForm {
  return {
    quotaMax: { custom: overrides.quota_max !== null, value: overrides.quota_max },
    quotaRenewalRate: {
      custom: overrides.quota_renewal_rate !== null,
      value: overrides.quota_renewal_rate,
    },
  };
}

// An unlimited allowance is never spent, so the look-to-book ratio doesn't apply while the form
// sets one.
export function setsUnlimitedAllowance(form: QuotaOverridesForm): boolean {
  return form.quotaMax.custom && form.quotaMax.value === UNLIMITED_QUOTA_MAX;
}

// Why the form can't be sent, or null when it can. The ratio is hidden under an unlimited
// allowance, so it isn't checked there either.
export function quotaOverridesError(form: QuotaOverridesForm): string | null {
  const quotaMaxError = overrideError(form.quotaMax, QUOTA_MAX_MINIMUM, QUOTA_MAX_NAME);
  if (quotaMaxError) return quotaMaxError;
  if (setsUnlimitedAllowance(form)) return null;
  return (
    overrideError(form.quotaRenewalRate, QUOTA_RENEWAL_RATE_MINIMUM, QUOTA_RENEWAL_RATE_NAME) ??
    ratioAboveAllowanceError(form)
  );
}

// A booking never refills past the allowance, so a ratio above it acts as one equal to it and
// only overstates what a booking earns back. It is refused only when the form sets both, as an
// inherited allowance isn't known here.
function ratioAboveAllowanceError(form: QuotaOverridesForm): string | null {
  const { quotaMax, quotaRenewalRate } = form;
  if (!quotaMax.custom || !quotaRenewalRate.custom) return null;
  if (quotaMax.value === null || quotaRenewalRate.value === null) return null;
  if (quotaRenewalRate.value <= quotaMax.value) return null;
  return `${QUOTA_RENEWAL_RATE_NAME} can't be more than the shopping allowance.`;
}

function overrideError(field: QuotaOverrideField, minimum: number, name: string): string | null {
  if (!field.custom) return null;
  if (field.value === null || !Number.isInteger(field.value)) {
    return `${name} must be a whole number.`;
  }
  if (field.value < minimum) return `${name} can't be below ${minimum}.`;
  return null;
}

// The ratio the form overrides with, or null when it inherits the policy's. Under an unlimited
// allowance the hidden ratio is unchecked, so one left invalid there is dropped rather than sent;
// a valid one is kept for when the allowance is limited again.
function quotaRenewalRateOverride(form: QuotaOverridesForm): number | null {
  const field = form.quotaRenewalRate;
  if (!field.custom) return null;
  if (overrideError(field, QUOTA_RENEWAL_RATE_MINIMUM, QUOTA_RENEWAL_RATE_NAME)) return null;
  return field.value;
}

// The overrides to request a connection with: an unticked one is left out, so it inherits the
// policy's.
export function requestedQuotaOverrides(
  form: QuotaOverridesForm
): Partial<ApplicationQuotaOverrides> {
  const overrides: Partial<ApplicationQuotaOverrides> = {};
  if (form.quotaMax.custom) overrides.quota_max = form.quotaMax.value;
  const quotaRenewalRate = quotaRenewalRateOverride(form);
  if (quotaRenewalRate !== null) overrides.quota_renewal_rate = quotaRenewalRate;
  return overrides;
}

// The overrides to replace a connection's with: an unticked one is null, which inherits the
// policy's again.
export function replacementQuotaOverrides(form: QuotaOverridesForm): ApplicationQuotaOverrides {
  return {
    quota_max: form.quotaMax.custom ? form.quotaMax.value : null,
    quota_renewal_rate: quotaRenewalRateOverride(form),
  };
}

// The two quota overrides an agency sets on a connection, each either its own value or the
// policy's. The form is edited in place, so its owner reads the values straight off it.
@Component({
  selector: 'app-quota-overrides',
  imports: [NgIf, FormsModule],
  templateUrl: './quota-overrides.component.html',
})
export class QuotaOverridesComponent {
  @Input({ required: true }) form!: QuotaOverridesForm;
  // Prefixes the inputs' ids, which must be unique on the page.
  @Input({ required: true }) idPrefix!: string;

  readonly quotaMaxMinimum = QUOTA_MAX_MINIMUM;
  readonly quotaRenewalRateMinimum = QUOTA_RENEWAL_RATE_MINIMUM;

  get error(): string | null {
    return quotaOverridesError(this.form);
  }

  get unlimitedAllowance(): boolean {
    return setsUnlimitedAllowance(this.form);
  }
}
